import { Alert, Box, CircularProgress, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { ArrowLeftIcon, RefreshCwIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import {
  accountColumns,
  featureColumns,
  monthColumns,
  userColumns,
} from '@/components/credit-consumption/detailColumns';
import { DetailTable } from '@/components/credit-consumption/DetailTable';
import {
  credit,
  fetchFailureMessage,
  NONE,
  percent,
  reflectedText,
} from '@/components/credit-consumption/display';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getConsumptionDetail,
  type ConsumptionDetail,
  type FeatureRow,
} from '@/lib/api/credit-consumption';
import { toFailure } from '@/lib/api/credit-contracts';
import { monthLabel } from '@/lib/credit-contracts/month';

/** 取得の状態。月を選び直して取得し直す間も、前に取得した値（data）を表示したままにする */
interface Load {
  data: ConsumptionDetail | null;
  loading: boolean;
  error: string | null;
}

/** URL の month のうち、`YYYY-MM` の形のものだけを API に渡す（それ以外は当月を選ぶ） */
const monthParam = (value: string | null) => (value && /^\d{4}-\d{2}$/.test(value) ? value : null);

type BreakdownTab = 'users' | 'accounts' | 'features';

/** 機能ごとの内訳の行のキー。機能コードは、1つのアカウントの中で1つの行にだけ入る */
const featureRowKey = (r: FeatureRow) =>
  `${r.account_id}:${r.kind}:${r.feature_category_code ?? r.feature_code ?? ''}`;

/** 契約詳細のパス。契約詳細から戻る先を、この消費詳細（消費一覧のクエリを含む）にする */
const contractDetailPath = (companyId: string, back: string) =>
  `/credit/contracts/${encodeURIComponent(companyId)}?${new URLSearchParams({ back }).toString()}`;

/**
 * 消費詳細（FKV3-13046）。消費一覧で企業を選ぶと移る。
 * 1つの企業について、当月と過去の月の推移と、選んだ月のユーザーごと・アカウントごと・機能ごとの内訳を表示する。
 * 推移の表で月を選ぶと、その月の内訳を表示する。初めは消費一覧で指定した月（URL の month）を選び、なければ当月を選ぶ。
 * 選んだ月は URL に持ち、契約詳細から戻ったときも保つ。
 * 内訳は選んだ月の分だけ API が返すため、月を選び直すたびに、推移も含めて取得し直す（画面の値を1回の読み取りから求める）。
 */
export function ConsumptionDetailPage() {
  const { companyId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { getAuthHeader } = useAuthHeader();
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const [detail, setDetail] = useState<Load>({ data: null, loading: true, error: null });
  const [tab, setTab] = useState<BreakdownTab>('users');
  // 消費一覧から移ったときは、一覧の状態を移る前のままにして戻る（消費一覧のクエリは URL の list に持つ）
  const list = searchParams.get('list');
  const listSearch = list ? `?${list}` : '';
  const selected = monthParam(searchParams.get('month'));

  // 月を続けて選んだときに、前の月の応答で上書きしないよう、最後に始めた取得だけを反映する
  const requestRef = useRef(0);
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setDetail((d) => ({ ...d, loading: true, error: null }));
    try {
      const data = await getConsumptionDetail(companyId, selected, authRef.current());
      if (request === requestRef.current) setDetail({ data, loading: false, error: null });
    } catch (e) {
      if (request === requestRef.current) {
        setDetail((d) => ({ ...d, loading: false, error: fetchFailureMessage(toFailure(e)) }));
      }
    }
  }, [companyId, selected]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = detail.data;
  // 取得し直している間は、選んだ月の行を先に選んだ状態にする
  const selectedMonth = detail.loading && selected ? selected : (data?.selected_month ?? null);
  const selectMonth = (m: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('month', m);
    setSearchParams(params, { replace: true });
  };
  const months = useMemo(
    () => (data ? monthColumns(data.categories, data.execution_categories) : []),
    [data],
  );
  // 推移の表は最大37行あるので、開いたときに選んだ月の行が表の中で見える位置にする。
  // 月を選び直して取得し直したときは、位置を動かさない
  const trendRef = useRef<HTMLDivElement>(null);
  const centeredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data || centeredRef.current === data.company.id) return;
    const container = trendRef.current?.querySelector<HTMLElement>('.MuiTableContainer-root');
    const row = container?.querySelector<HTMLElement>('tr[aria-selected="true"]');
    if (!container || !row) return;
    centeredRef.current = data.company.id;
    container.scrollTop = Math.max(0, row.offsetTop - container.clientHeight / 2);
  }, [data]);
  const users = useMemo(() => (data ? userColumns(data.execution_categories) : []), [data]);
  const accounts = useMemo(() => (data ? accountColumns(data.execution_categories) : []), [data]);
  const features = useMemo(() => (data ? featureColumns(data.execution_categories) : []), [data]);

  return (
    <AppLayout title="消費一覧 / 消費詳細" showSearch={false}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="baseline" columnGap={2} rowGap={1} flexWrap="wrap">
          <Link
            to={`/credit/consumption${listSearch}`}
            state={{ focusCompany: companyId }}
            className="inline-flex items-center gap-1 self-center text-sm text-blue-700 hover:underline"
          >
            <ArrowLeftIcon size={16} />
            消費一覧へ
          </Link>
          {data && (
            <>
              <Typography variant="subtitle1" component="h2" fontWeight="bold">
                {data.company.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.company.id}
              </Typography>
              <Link
                to={contractDetailPath(data.company.id, location.pathname + location.search)}
                className="text-sm text-blue-700 hover:underline"
              >
                契約詳細へ
              </Link>
              <Typography variant="body2" color="text.secondary" ml="auto">
                反映時点 {reflectedText(data.reflected_at)}
              </Typography>
            </>
          )}
        </Box>

        {detail.loading && !data && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {detail.error && (
          <Alert
            severity="error"
            action={
              <Button size="sm" variant="outline" onClick={() => void load()}>
                <RefreshCwIcon />
                再読み込み
              </Button>
            }
          >
            {detail.error}
          </Alert>
        )}
        {data && (
          <>
            <CurrentSummary data={data} />
            <Box ref={trendRef}>
              <Typography component="h3" variant="body2" fontWeight="bold" mb={1}>
                月ごとの推移
              </Typography>
              <DetailTable
                ariaLabel="月ごとの推移"
                columns={months}
                rows={data.months}
                rowKey={(r) => r.month}
                emptyMessage="表示する月はありません。"
                sortable={false}
                maxHeight="45vh"
                selectedKey={selectedMonth ?? undefined}
                onRowClick={(r) => selectMonth(r.month)}
              />
            </Box>
            {selectedMonth && (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  borderBottom={1}
                  borderColor="divider"
                >
                  <Typography component="h3" variant="body2" fontWeight="bold">
                    {monthLabel(selectedMonth)}の内訳
                  </Typography>
                  <Tabs
                    value={tab}
                    onChange={(_, next: BreakdownTab) => setTab(next)}
                    sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
                  >
                    <Tab value="users" label="ユーザーごと" />
                    <Tab value="accounts" label="アカウントごと" />
                    <Tab value="features" label="機能ごと" />
                  </Tabs>
                </Box>
                {detail.loading && <LinearProgress aria-label="取得中" />}
                <Box mt={1} sx={{ opacity: detail.loading ? 0.5 : 1 }}>
                  {tab === 'users' && (
                    <DetailTable
                      key={`users:${data.selected_month}`}
                      ariaLabel={`${monthLabel(data.selected_month)}のユーザーごとの内訳`}
                      columns={users}
                      rows={data.users}
                      rowKey={(r) => r.key}
                      initialSort={{ key: 'total', dir: 'desc' }}
                      emptyMessage="この月の実行消費はありません。"
                      maxHeight="60vh"
                    />
                  )}
                  {tab === 'accounts' && (
                    <DetailTable
                      key={`accounts:${data.selected_month}`}
                      ariaLabel={`${monthLabel(data.selected_month)}のアカウントごとの内訳`}
                      columns={accounts}
                      rows={data.accounts}
                      rowKey={(r) => r.account_id}
                      initialSort={{ key: 'code', dir: 'asc' }}
                      emptyMessage="この月の時点値と実行消費はありません。"
                      maxHeight="60vh"
                    />
                  )}
                  {tab === 'features' && (
                    <DetailTable
                      key={`features:${data.selected_month}`}
                      ariaLabel={`${monthLabel(data.selected_month)}の機能ごとの内訳`}
                      columns={features}
                      rows={data.features}
                      rowKey={featureRowKey}
                      initialSort={{ key: 'account_code', dir: 'asc' }}
                      subRows={(r) => r.features}
                      emptyMessage="この月の実行消費はありません。"
                      maxHeight="60vh"
                    />
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </AppLayout>
  );
}

/** 当月の消費と月末の着地見込みを、それぞれの契約に対する割合とあわせて示す */
function CurrentSummary({ data }: { data: ConsumptionDetail }) {
  const c = data.current;
  const item = (term: string, value: string) => (
    <Box display="flex" columnGap={1}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {term}
      </Typography>
      <Typography component="dd" variant="body2" m={0} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
  return (
    <Box
      component="dl"
      display="flex"
      alignItems="baseline"
      columnGap={3}
      rowGap={0.5}
      flexWrap="wrap"
      m={0}
    >
      <Typography component="h3" variant="body2" fontWeight="bold">
        当月 {monthLabel(data.current_month)}
      </Typography>
      {item('消費クレジット数', c.consumption ? `${credit(c.consumption)}クレジット` : NONE)}
      {item('契約に対する割合', c.ratio ? `${percent(c.ratio)}%` : NONE)}
      {item('月末の着地見込み', c.forecast ? `${credit(c.forecast)}クレジット` : NONE)}
      {item('月末の着地見込みの割合', c.forecast_ratio ? `${percent(c.forecast_ratio)}%` : NONE)}
    </Box>
  );
}
